import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Shield, Package } from 'lucide-react';
import { useDealerAuth } from '../DealerAuthContext';
import { dealerGetOrders } from '../../services/api';

const STATUS_COLORS = {
  // New unified pipeline statuses
  new: 'text-zinc-400 bg-zinc-800 border-zinc-700',
  payment_pending: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
  payment_verified: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  validation_pending: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
  validation_hold: 'text-red-400 bg-red-500/10 border-red-500/20',
  ready_for_production: 'text-green-400 bg-green-500/10 border-green-500/20',
  on_hold: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
  shipped: 'text-teal-400 bg-teal-500/10 border-teal-500/20',
  delivered: 'text-green-400 bg-green-500/10 border-green-500/20',
  cancelled: 'text-red-400 bg-red-500/10 border-red-500/20',
  // Legacy statuses
  pending: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
  confirmed: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  in_production: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
};

const STATUS_LABELS = {
  new: 'Received', payment_pending: 'Payment Pending', payment_verified: 'Payment Verified',
  validation_pending: 'In Review', validation_hold: 'Under Review',
  batched: 'Cut Sheet Queue',
  ready_for_production: 'In Production Queue', on_hold: 'On Hold',
  shipped: 'Shipped', delivered: 'Delivered', cancelled: 'Cancelled',
  pending: 'Pending', confirmed: 'Confirmed', in_production: 'In Production',
};

export default function DealerOrders() {
  const { token } = useDealerAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    dealerGetOrders(token)
      .then(data => setOrders(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div data-testid="dealer-orders-page">
      <div className="mb-6">
        <h1 className="font-bebas text-4xl text-white tracking-wider">ORDER HISTORY</h1>
        <p className="font-inter text-sm text-zinc-500 mt-1">{orders.length} total orders</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16 bg-[#1a1a1a] border border-white/10">
          <Package size={40} className="text-zinc-700 mx-auto mb-3" />
          <p className="font-bebas text-xl text-zinc-600 tracking-wider">NO ORDERS YET</p>
          <p className="font-inter text-sm text-zinc-600 mt-1">Browse the product catalog to place your first order</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map(o => (
            <div key={o._id} data-testid={`order-${o._id}`} className="bg-[#1a1a1a] border border-white/10 overflow-hidden">
              {/* Order header */}
              <button
                className="w-full flex items-center justify-between p-5 hover:bg-white/3 transition-colors"
                onClick={() => setExpanded(expanded === o._id ? null : o._id)}
              >
                <div className="flex items-center gap-4 text-left">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="font-inter text-sm font-semibold text-white">{o.order_number}</span>
                      <span className={`font-inter text-[10px] font-bold px-2 py-0.5 border uppercase tracking-wider ${STATUS_COLORS[o.status] || 'text-zinc-400 bg-zinc-800 border-zinc-700'}`}>
                        {STATUS_LABELS[o.status] || o.status}
                      </span>
                      {o.has_nfa_items && (
                        <span className="flex items-center gap-1 font-inter text-[9px] text-orange-400">
                          <Shield size={9} />NFA
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="font-inter text-xs text-zinc-500">{new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      <span className="font-inter text-xs text-zinc-600">·</span>
                      <span className="font-inter text-xs text-zinc-500">{o.items?.length} item{o.items?.length !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-bebas text-xl text-white tracking-wide">${o.subtotal?.toFixed(2)}</span>
                  {expanded === o._id ? <ChevronUp size={16} className="text-zinc-400" /> : <ChevronDown size={16} className="text-zinc-400" />}
                </div>
              </button>

              {/* Expanded detail */}
              {expanded === o._id && (
                <div className="border-t border-white/10 p-5">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Items */}
                    <div>
                      <h3 className="font-bebas text-lg text-white tracking-wider mb-3">ORDER ITEMS</h3>
                      <div className="space-y-2">
                        {o.items?.map((item, i) => (
                          <div key={i} className="flex items-center justify-between bg-[#141414] border border-white/5 p-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-[#0f0f0f] flex-shrink-0 overflow-hidden">
                                <img src={item.product_image} alt="" className="w-full h-full object-contain p-0.5" onError={e => { e.target.style.display = 'none'; }} />
                              </div>
                              <div>
                                <p className="font-inter text-sm text-white">{item.product_name}</p>
                                <p className="font-inter text-xs text-zinc-500">
                                  Qty: {item.quantity} × ${item.unit_price?.toFixed(2)}
                                  {item.nfa_item && <span className="ml-2 text-orange-400">NFA</span>}
                                </p>
                              </div>
                            </div>
                            <span className="font-bebas text-base text-white">${item.total?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
                        <span className="font-inter text-xs text-zinc-500 uppercase tracking-wider">Subtotal</span>
                        <span className="font-bebas text-xl text-orange-500 tracking-wide">${o.subtotal?.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Shipping & notes */}
                    <div>
                      <h3 className="font-bebas text-lg text-white tracking-wider mb-3">SHIPPING & NOTES</h3>
                      {o.shipping_address && (
                        <div className="bg-[#141414] border border-white/5 p-3 mb-3">
                          <p className="font-inter text-xs text-zinc-500 uppercase tracking-wider mb-1">Ship To</p>
                          <p className="font-inter text-sm text-white">{o.shipping_address.address}</p>
                          <p className="font-inter text-sm text-white">{o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.zip}</p>
                        </div>
                      )}
                      {o.notes && (
                        <div className="bg-[#141414] border border-white/5 p-3">
                          <p className="font-inter text-xs text-zinc-500 uppercase tracking-wider mb-1">Notes</p>
                          <p className="font-inter text-sm text-zinc-300">{o.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
